/**
 * Typography variant → Tailwind class mapping.
 * These classes match the utilities defined in achieve-theme.js.
 *
 * Each key maps to its corresponding Tailwind utility class name
 * (registered via addUtilities in the tailwind config).
 *
 * NOTE: Preflight is off in Tailwind v4 by omitting the preflight import in
 * `src/app/global.css` (see ADR-004). Browser-default margins on block elements
 * (`<p>`, `<h1>`–`<h6>`, `<ul>`, `<ol>`, etc.) are NOT reset. MUI Typography
 * sets `margin: 0` internally.
 *
 * The margin reset is now applied as a BASE_MARGIN_RESET class in the
 * component itself rather than baked into variant classes.  This prevents
 * shorthand `m-0` / `lg:m-0` from overriding consumer-provided directional
 * margin utilities like `mt-6` at responsive breakpoints.
 */

/**
 * Applied once on the rendered element to zero out browser-default margins
 * on block elements (`<p>`, `<h1>`, etc.) when preflight is disabled.
 *
 * Uses individual logical-property utilities so that a consumer `mt-*`,
 * `mb-*`, `ml-*`, or `mr-*` class can override a single side without
 * fighting the shorthand `m-0` at any breakpoint.
 */
export const BASE_MARGIN_RESET = 'mt-0 mr-0 mb-0 ml-0'

export const VARIANT_CLASS_MAP = {
  bodyXs: 'bodyXs',
  bodySm: 'bodySm',
  bodyMd: 'bodyMd',
  bodyLg: 'bodyLg',
  labelSm: 'labelSm',
  labelMd: 'labelMd',
  labelLg: 'labelLg',
  headingSm: 'headingSm',
  headingMd: 'headingMd',
  headingLg: 'headingLg',
  headingXl: 'headingXl',
  displayXs: 'displayXs',
  displaySm: 'displaySm',
  displayMd: 'displayMd',
  displayLg: 'displayLg',
  displayXl: 'displayXl',
}

/**
 * Responsive variant class mapping.
 * Returns the desktop (lg:) variant class when a `mobileVariant` is supplied.
 * Includes `lg:m-0` to maintain margin reset at the desktop breakpoint.
 *
 * NOTE: Class names must be spelled out as full literals so Tailwind's
 * content scanner can detect them (dynamic concatenation is invisible to JIT).
 */
export const RESPONSIVE_VARIANT_CLASS_MAP = {
  bodyXs: 'lg:bodyXs',
  bodySm: 'lg:bodySm',
  bodyMd: 'lg:bodyMd',
  bodyLg: 'lg:bodyLg',
  labelSm: 'lg:labelSm',
  labelMd: 'lg:labelMd',
  labelLg: 'lg:labelLg',
  headingSm: 'lg:headingSm',
  headingMd: 'lg:headingMd',
  headingLg: 'lg:headingLg',
  headingXl: 'lg:headingXl',
  displayXs: 'lg:displayXs',
  displaySm: 'lg:displaySm',
  displayMd: 'lg:displayMd',
  displayLg: 'lg:displayLg',
  displayXl: 'lg:displayXl',
}

/**
 * Default rich-text block ↔ variant/component mapping.
 * Replaces the MUI-centric RICH_TEXT constant from the original Typography.
 */
export const RICH_TEXT = {
  paragraph: { variant: 'bodyLg', component: 'p' },
  'heading-1': { variant: 'displayXl', component: 'h1' },
  'heading-2': { variant: 'displayXl', component: 'h2' },
  'heading-3': { variant: 'displayXl', component: 'h3' },
  'heading-4': { variant: 'displayXl', component: 'h4' },
  'heading-5': { variant: 'displayLg', component: 'h5' },
  'heading-6': { variant: 'headingLg', component: 'h6' },
  'ordered-list': { component: 'ol' },
  'unordered-list': { component: 'ul' },
  'list-item': { component: 'li' },
  hr: {},
  'embedded-asset-block': {},
  table: { variant: 'bodyLg', component: 'table' },
  'table-row': { variant: 'bodyLg', component: 'tr' },
  'table-cell': { variant: 'bodyLg', component: 'td' },
  'table-header-cell': { variant: 'bodyLg', component: 'th' },
  hyperlink: { component: 'a' },
  'asset-hyperlink': { component: 'a' },
  bold: { variant: 'bodyLg', component: 'strong' },
}

/**
 * Variants that can be explicitly set as mobileVariant.
 */
export const EXPLICIT_MOBILE_OVERRIDES = [
  'bodyLg',
  'bodyMd',
  'bodySm',
  'bodyXs',
  'displayLg',
  'displayMd',
  'displaySm',
  'headingLg',
  'headingMd',
  'headingSm',
  'labelMd',
]
